import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Refresh, FileDownload, ArrowBack } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function CashBook() {
    const [dataItem, setDataItem] = useState([]);
    const history = useHistory();
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedChildOption, setSelectedChildOption] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, token, branchId, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const fetchTableData = async () => {
        if (new Date(fromDate) > new Date(toDate)) {
            alert('From Date cannot be greater than To Date');
            return;
        }
        setDataItem([]);
        
        // Delay showing loading overlay to prevent flash on quick loads     
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading cash book data...');
        }, 300);
        
        try {
            const response = await fetchData('ReportApi', `/CashBook/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Packing Slip Details:', error);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const formatMonth = (dateString) => {
        const date = new Date(dateString);
        const options = { year: 'numeric', month: 'long' };
        return date.toLocaleDateString('en-GB', options);
    };

    const getMonthKey = (dateString) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Cash Book');
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 3 }
        ];
        let bal = 0;

        const computeBalanceForExcel = (data) => {
            if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
                if (data.credit) bal += Math.abs(data.credit);
                if (data.debit) bal -= Math.abs(data.debit);
            }

            if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
                return bal >= 0 ? `${Math.abs(bal).toFixed(2)} Cr` : `${Math.abs(bal).toFixed(2)} Dr`;
            }

            return '';
        };

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Cash_Book_From_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Company Name Row
        worksheet.mergeCells('A1:E1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Title row
        worksheet.mergeCells('A2:E2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Cash Book - From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headers = ['Date', 'Description', 'Debit', 'Credit', 'Balance'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        // Group data by month
        const groupedByMonth = filteredDataItem.reduce((groups, item) => {
            const monthKey = getMonthKey(item.billdate);
            if (!groups[monthKey]) groups[monthKey] = [];
            groups[monthKey].push(item);
            return groups;
        }, {});

        // Add data rows grouped by month
        Object.entries(groupedByMonth).forEach(([monthKey, items], monthIndex, allMonths) => {
            const isLastGroup = monthIndex === allMonths.length - 1;
            const monthName = items.length > 0 ? formatMonth(items[0].billdate) : monthKey;

            // Month header row
            const monthHeaderRow = worksheet.addRow([monthName, '', '', '', '']);
            monthHeaderRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe0f2f1' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                cell.border = thinBorder;
            });
            worksheet.mergeCells(`A${monthHeaderRow.number}:E${monthHeaderRow.number}`);

            // Month entries
            items.forEach((rowData) => {
                if (rowData.description1 === 'Total') return;

                let rowValues;

                if (rowData.description1 === 'Opening Balance' || rowData.description1 === 'Closing Balance') {
                    rowValues = [
                        '',
                        rowData.description1,
                        rowData.debit != null ? rowData.debit.toFixed(2) : '',
                        rowData.credit != null ? rowData.credit.toFixed(2) : '',
                        rowData.description1 === 'Opening Balance' ? computeBalanceForExcel(rowData) : ''
                    ];
                } else {
                    const desc = rowData.description1 + (rowData.billno ? ` - (${rowData.billno})` : '');
                    rowValues = [
                        formatDate(rowData.billdate),
                        desc,
                        rowData.debit != null ? rowData.debit.toFixed(2) : '',
                        rowData.credit != null ? rowData.credit.toFixed(2) : '',
                        computeBalanceForExcel(rowData)
                    ];
                }

                const row = worksheet.addRow(rowValues);

                row.eachCell((cell, colNumber) => {
                    if ([3, 4, 5].includes(colNumber)) {
                        cell.alignment = { vertical: 'middle', horizontal: 'right' };
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    }
                    cell.border = thinBorder;
                });

                if (rowData.description1 === 'Closing Balance') {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFd4edda' },
                        };
                        cell.font = { bold: true };
                        cell.border = thinBorder;
                    });
                }
            });

            // Month subtotal
            if (!isLastGroup) {
                const validItems = items.filter(
                    (d) =>
                        d.description1 !== "Opening Balance" &&
                        d.description1 !== "Closing Balance" &&
                        d.description1 !== "Total"
                );

                if (validItems.length > 0) {
                    const monthDebitTotal = validItems.reduce((sum, d) => sum + (d.debit || 0), 0);
                    const monthCreditTotal = validItems.reduce((sum, d) => sum + (d.credit || 0), 0);

                    const subtotalRow = worksheet.addRow([
                        '',
                        `Sub Total (${monthName})`,
                        monthDebitTotal.toFixed(2),
                        monthCreditTotal.toFixed(2),
                        ''
                    ]);

                    subtotalRow.eachCell((cell, colNumber) => {
                        cell.font = { bold: true };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf3f4f6' } };
                        if ([3, 4].includes(colNumber)) {
                            cell.alignment = { horizontal: 'right', vertical: 'middle' };
                            cell.numFmt = '#,##0.00';
                        } else {
                            cell.alignment = { horizontal: 'left', vertical: 'middle' };
                        }
                        cell.border = thinBorder;
                    });
                }
            }
        });

        worksheet.columns = [
            { width: 14 },
            { width: 35 },
            { width: 15 },
            { width: 15 },
            { width: 20 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    let bal = 0;

    const computeBalance = (data) => {
        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            if (data.credit) {
                bal += Math.abs(data.credit);
            }
            if (data.debit) {
                bal -= Math.abs(data.debit);
            }
        }

        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            const absBalance = Math.abs(bal).toFixed(2);
            const suffix = bal >= 0 ? 'Cr' : 'Dr';
            return `₹${absBalance} ${suffix}`;
        }

        return '';
    };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 9999,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: '#4e342e',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#4e342e',
                                fontWeight: 600,
                                fontSize: '1rem'
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #5d4037 0%, #795548 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Cash Book</span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input type="date" value={fromDate} onChange={handleFromDateChange} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={handleToDateChange} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, width: 140 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchTableData} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExportExcel} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                            <FileDownload sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Table Section */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
                    <TableContainer component={Paper} style={{
                        boxShadow: 'none',
                        borderRadius: '0',
                        overflow: isMobile ? 'auto' : 'hidden'
                    }}>
                        <Table sx={{ minWidth: isMobile ? 700 : 650 }} size="small" aria-label="cashbook table">
                            <TableHead>
                                <TableRow style={{
                                    background: '#795548',
                                    height: '24px'
                                }}>
                                    {['Date', 'Description', 'Debit', 'Credit', 'Balance'].map((col) => (
                                        <TableCell
                                            key={col}
                                            style={{
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: isMobile ? '8px' : '9px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.2px',
                                                padding: isMobile ? '2px 4px' : '2px 6px',
                                                textAlign: col === 'Date' || col === 'Description' ? 'left' : 'right',
                                                height: '24px'
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(
                                    filteredDataItem.reduce((groups, item) => {
                                        const monthKey = getMonthKey(item.billdate);
                                        if (!groups[monthKey]) groups[monthKey] = [];
                                        groups[monthKey].push(item);
                                        return groups;
                                    }, {})
                                ).map(([monthKey, items], monthIndex, allMonths) => {
                                    const isLastGroup = monthIndex === allMonths.length - 1;

                                    const validItems = items.filter(
                                        (d) =>
                                            d.description1 !== "Opening Balance" &&
                                            d.description1 !== "Closing Balance" &&
                                            d.description1 !== "Total"
                                    );

                                    const monthDebitTotal = validItems.reduce((sum, d) => sum + (d.debit || 0), 0);
                                    const monthCreditTotal = validItems.reduce((sum, d) => sum + (d.credit || 0), 0);

                                    // Get month name from first item
                                    const monthName = items.length > 0 ? formatMonth(items[0].billdate) : monthKey;

                                    return (
                                        <React.Fragment key={monthIndex}>
                                            {/* MONTH HEADER ROW */}
                                            <TableRow sx={{ backgroundColor: "#e0f2f1" }}>
                                                <TableCell
                                                    colSpan={5}
                                                    sx={{ 
                                                        fontWeight: "bold", 
                                                        fontSize: isMobile ? "10px" : "12px", 
                                                        color: "#004d40", 
                                                        padding: isMobile ? "4px" : "6px 8px" 
                                                    }}
                                                >
                                                    {monthName}
                                                </TableCell>
                                            </TableRow>

                                            {/* ENTRIES FOR THAT MONTH */}
                                            {items.map((data, index) => {
                                                if (data.description1 === "Total") return null;

                                                return (
                                                    <TableRow
                                                        key={data.id || `${monthKey}-${index}`}
                                                        sx={{
                                                            '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9',
                                                                transition: 'all 0.15s ease-in-out'
                                                            },
                                                            borderBottom: '1px solid #f1f5f9',
                                                            height: '28px'
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                                {formatDate(data.billdate)}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.description1}{data.billno ? ` - (${data.billno})` : ''}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                                                                {data.debit != null && data.debit > 0 ? `₹${Math.abs(data.debit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#059669' }}>
                                                                {data.credit != null && data.credit > 0 ? `₹${Math.abs(data.credit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#374151' }}>
                                                                {computeBalance(data)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                            {/* MONTH SUBTOTAL ROW */}
                                            {!isLastGroup &&
                                                validItems.length > 0 &&
                                                !items.some(
                                                    (d) =>
                                                        d.description1 === "Opening Balance" ||
                                                        d.description1 === "Closing Balance" ||
                                                        d.description1 === "Total"
                                                ) && (
                                                    <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                                                        <TableCell colSpan={2} sx={{ fontWeight: "bold", fontSize: isMobile ? "10px" : "11px", padding: isMobile ? '4px' : '4px 6px' }}>
                                                            Sub Total ({monthName})
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", fontWeight: "bold", fontSize: isMobile ? "10px" : "11px", padding: isMobile ? '4px' : '4px 6px' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#dc2626' }}>
                                                                ₹{Math.abs(monthDebitTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", fontWeight: "bold", fontSize: isMobile ? "10px" : "11px", padding: isMobile ? '4px' : '4px 6px' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#059669' }}>
                                                                ₹{Math.abs(monthCreditTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", fontWeight: "bold", fontSize: isMobile ? "10px" : "11px", padding: isMobile ? '4px' : '4px 6px' }}>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
}

export default CashBook;



